import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../api/auth'
import { useAuth } from '../contexts/AuthContext'
import { useSignature } from '../contexts/SignatureContext'
import { UserContextResponse, ContextSelectionRequest, UserContext, NivelAcesso } from '../types/auth'
import { Building2, Search, CheckCircle2, ArrowRight, ShieldAlert, Sun, Moon, Loader2 } from 'lucide-react'
import { getTenantLogoUrl } from '../utils/tenantLogo'

const ContextSelectionPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { updateContext } = useAuth()
  const { mode, toggleMode } = useSignature()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [context, setContext] = useState<UserContextResponse | null>(null)
  const [selectedRole] = useState<'USUARIO'>('USUARIO')
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let isMounted = true
    const loadContext = async () => {
      try {
        setLoading(true)
        setError(null)
        const contextData = await authApi.getUserContext()
        if (!isMounted) return

        setContext(contextData)

        // Se houver apenas 1 empresa, entra nela diretamente
        if (contextData.empresas && contextData.empresas.length === 1) {
          await handleSubmit('USUARIO', contextData.empresas[0].id_empresa, contextData)
        } else if (contextData.empresas && contextData.empresas.length > 0) {
          // Pré-seleciona a primeira empresa em ordem alfabética
          const sorted = [...contextData.empresas].sort((a, b) =>
            (a.nome_empresa || '').localeCompare(b.nome_empresa || '', 'pt-BR', { sensitivity: 'base' })
          )
          setSelectedEmpresaId(sorted[0].id_empresa)
        }
      } catch (err: any) {
        if (!isMounted) return
        setError(err.response?.data?.detail || t('context.loadError') || 'Erro ao carregar contexto de empresas')
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadContext()
    return () => {
      isMounted = false
    }
  }, [t])

  // Empresas ordenadas alfabeticamente
  const sortedEmpresas = useMemo(() => {
    if (!context?.empresas) return []
    return [...context.empresas].sort((a, b) =>
      (a.nome_empresa || '').localeCompare(b.nome_empresa || '', 'pt-BR', { sensitivity: 'base' })
    )
  }, [context?.empresas])

  // Filtro de busca por nome ou CNPJ
  const filteredEmpresas = useMemo(() => {
    if (!searchTerm.trim()) return sortedEmpresas
    const term = searchTerm.toLowerCase().trim()
    return sortedEmpresas.filter(
      (e) =>
        (e.nome_empresa && e.nome_empresa.toLowerCase().includes(term)) ||
        (e.razao_social && e.razao_social.toLowerCase().includes(term)) ||
        (e.cnpj && e.cnpj.includes(term))
    )
  }, [sortedEmpresas, searchTerm])

  const handleSubmit = async (
    role?: 'SUPER_ADMIN' | 'ADMINISTRADOR' | 'USUARIO',
    empresaId?: number,
    currentContext?: UserContextResponse
  ) => {
    const finalRole = role || selectedRole
    const finalEmpresaId = empresaId || selectedEmpresaId
    const activeContext = currentContext || context

    if (!activeContext) return

    if (!finalEmpresaId) {
      setError(t('context.selectCompanyError') || 'Selecione uma empresa para continuar')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      let nivel_acesso_efetivo = activeContext.nivel_acesso || NivelAcesso.USUARIO_PADRAO

      const contextSelection: ContextSelectionRequest = {
        role: finalRole,
        empresa_id: finalEmpresaId,
      }

      await authApi.selectContext(contextSelection)

      const selectedEmpresa = activeContext.empresas.find((e) => e.id_empresa === finalEmpresaId)
      const userContext: UserContext = {
        role: finalRole,
        empresa_id: finalEmpresaId,
        nivel_acesso_efetivo,
        preferencias: selectedEmpresa?.preferencias || {},
        empresa_nome: selectedEmpresa?.nome_empresa || null,
        empresa_razao_social: selectedEmpresa?.razao_social || null,
        empresa_cnpj: selectedEmpresa?.cnpj || null,
        logo_url: selectedEmpresa?.logo_url || (selectedEmpresa?.preferencias as any)?.ui?.logoUrl || null,
      }

      updateContext(userContext)

      try {
        const ui = (selectedEmpresa?.preferencias as any)?.ui
        if (ui?.mode && (ui.mode === 'light' || ui.mode === 'dark') && ui.mode !== mode) {
          toggleMode()
        }
      } catch {}

      navigate('/', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.detail || t('context.saveError') || 'Erro ao definir empresa de trabalho')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50/50 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-6">
        <div className="flex flex-col items-center gap-4 bg-white/80 dark:bg-slate-900/80 p-8 rounded-2xl shadow-xl backdrop-blur-md">
          <Loader2 className="animate-spin text-blue-600 dark:text-blue-400 size-10" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
            Carregando empresas autorizadas...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50/50 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-4xl bg-white/95 dark:bg-slate-900/90 backdrop-blur-md rounded-2xl shadow-2xl border border-slate-200/80 dark:border-slate-800 p-6 sm:p-10 space-y-6 animate-fade-in-up">
        {/* Cabeçalho */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-xs font-semibold mb-2">
              <Building2 size={14} /> Seleção de Ambiente
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Escolha a Empresa de Trabalho
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Selecione o tenant que deseja gerenciar nesta sessão (ordem alfabética)
            </p>
          </div>

          <button
            onClick={toggleMode}
            type="button"
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          >
            {mode === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            <span>{mode === 'dark' ? 'Claro' : 'Escuro'}</span>
          </button>
        </div>

        {/* Mensagem de erro */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200 p-4 rounded-xl flex items-start gap-3 border border-red-200 dark:border-red-800/60 text-sm">
            <ShieldAlert size={18} className="shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <span>{error}</span>
          </div>
        )}

        {/* Campo de Busca Rápida */}
        {sortedEmpresas.length > 3 && (
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 size-4" />
            <input
              type="text"
              placeholder="Buscar por nome da empresa, razão social ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
            />
          </div>
        )}

        {/* Lista de Cards de Empresa em Ordem Alfabética */}
        {filteredEmpresas.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[380px] overflow-y-auto pr-1 scrollbar-standard">
            {filteredEmpresas.map((empresa) => {
              const isSelected = selectedEmpresaId === empresa.id_empresa
              return (
                <div
                  key={empresa.id_empresa}
                  onClick={() => setSelectedEmpresaId(empresa.id_empresa)}
                  className={`relative flex flex-col justify-between p-4 rounded-xl border-2 transition-all cursor-pointer select-none group ${
                    isSelected
                      ? 'border-blue-600 bg-blue-50/60 dark:bg-blue-950/40 shadow-md ring-1 ring-blue-500/30'
                      : 'border-slate-200/80 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/30 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      {(() => {
                        const tenantLogo = getTenantLogoUrl(empresa);
                        if (tenantLogo) {
                          return (
                            <img 
                              src={tenantLogo} 
                              alt={empresa.nome_empresa} 
                              className="w-9 h-9 rounded-lg object-contain bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-0.5"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling;
                                if (fallback) fallback.classList.remove('hidden');
                              }}
                            />
                          );
                        }
                        return null;
                      })()}
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm transition-colors ${
                          getTenantLogoUrl(empresa) ? 'hidden' : ''
                        } ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50 group-hover:text-blue-600'
                        }`}
                      >
                        {empresa.nome_empresa ? empresa.nome_empresa.charAt(0).toUpperCase() : 'E'}
                      </div>
                      {isSelected && (
                        <CheckCircle2 size={18} className="text-blue-600 dark:text-blue-400 shrink-0" />
                      )}
                    </div>

                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-base line-clamp-1">
                        {empresa.nome_empresa}
                      </h3>
                      {empresa.razao_social && empresa.razao_social !== empresa.nome_empresa && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                          {empresa.razao_social}
                        </p>
                      )}
                    </div>
                  </div>

                  {empresa.cnpj && (
                    <div className="pt-3 mt-2 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                      <span>CNPJ</span>
                      <span className="font-mono">{empresa.cnpj}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-10 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
            <Building2 className="mx-auto text-slate-400 size-8 mb-2" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {searchTerm ? 'Nenhuma empresa encontrada com este filtro.' : 'Nenhuma empresa disponível para este usuário.'}
            </p>
          </div>
        )}

        {/* Rodapé e Ação de Entrada */}
        <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <span className="text-xs text-slate-400 dark:text-slate-500">
            Empresas disponíveis: <strong className="text-slate-700 dark:text-slate-300">{sortedEmpresas.length}</strong>
          </span>

          <button
            onClick={() => handleSubmit()}
            disabled={submitting || !selectedEmpresaId}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 text-white font-semibold text-sm shadow-md transition-all cursor-pointer"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin size-4" /> Entrando...
              </>
            ) : (
              <>
                Confirmar Empresa <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ContextSelectionPage
